import PropTypes from 'prop-types';
import React from 'react';
// @mui
import { Avatar, Box, Card, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// utils
import { fShortenNumber } from '../../../../utils/formatNumber';

// Styled Components
const StyledCard = styled(Card)(({ theme, status }) => {
  const base = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '12px',
    gap: '14px',
    minHeight: '76px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  };

  if (status === 'dayoff') {
    return {
      ...base,
      opacity: 0.6,
      border: `1px dashed ${theme.palette.error.main}`,
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.06)' : 'rgba(244, 67, 54, 0.04)',
    };
  }

  if (status === 'offshift') {
    return {
      ...base,
      opacity: 0.7,
      border: `1px dashed ${theme.palette.warning.main}`,
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.06)' : 'rgba(255, 152, 0, 0.04)',
    };
  }

  if (status === 'free') {
    return {
      ...base,
      border: `2px solid #4caf50`,
      backgroundColor: 'rgba(76, 175, 80, 0.08)',
    };
  }

  return {
    ...base,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  };
});

const RankingBadge = styled(Box)(({ position }) => {
  let backgroundColor = '#9e9e9e'; // Cinza padrão
  let color = '#fff';

  if (position === 1) {
    backgroundColor = '#ffd700'; // Ouro
    color = '#000';
  } else if (position === 2) {
    backgroundColor = '#c0c0c0'; // Prata
    color = '#000';
  } else if (position === 3) {
    backgroundColor = '#cd7f32'; // Bronze
    color = '#fff';
  }

  return {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: backgroundColor,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 700,
    flexShrink: 0,
    border: position <= 3 ? '3px solid rgba(255, 255, 255, 0.3)' : 'none',
    boxShadow: position <= 3 ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
  };
});

const StatusIndicator = styled(Box)(({ status }) => {
  let backgroundColor = '#4caf50'; // Verde - Livre

  if (status === 'busy') {
    backgroundColor = '#f44336'; // Vermelho - Ocupado
  } else if (status === 'presencial') {
    backgroundColor = '#ff9800'; // Laranja - Presencial
  } else if (status === 'dayoff') {
    backgroundColor = '#f44336'; // Vermelho - Em folga
  } else if (status === 'offshift') {
    backgroundColor = '#ff9800'; // Laranja - Fora do plantão
  }

  return {
    width: '5px',
    height: '48px',
    backgroundColor: backgroundColor,
    borderRadius: '3px',
    flexShrink: 0,
  };
});

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '48px',
  height: '48px',
  border: `2px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

const UserInfo = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const StatusLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const TicketsInfo = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  flexShrink: 0,
  gap: '2px',
}));

const TicketRow = styled(Box)(({ onClick }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  cursor: onClick ? 'pointer' : 'default',
  transition: 'all 0.15s',
  '&:hover': onClick ? {
    opacity: 0.8,
  } : {},
}));

const TicketCount = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1,
}));

const TicketLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

const PresencialRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '4px',
}));

const PresencialCount = styled(Typography)(() => ({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#ff9800',
  lineHeight: 1,
}));

const PresencialLabel = styled(Typography)(() => ({
  fontSize: '0.65rem',
  fontWeight: 500,
  color: '#ff9800',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

// ----------------------------------------------------------------------

UserCard.propTypes = {
  user: PropTypes.object.isRequired,
  rankingMode: PropTypes.bool,
  position: PropTypes.number,
  onTicketClick: PropTypes.func,
  isOnDayOff: PropTypes.bool,
  isOnShift: PropTypes.bool,
};

function UserCard({ user, rankingMode = false, position = 0, onTicketClick, isOnDayOff = false, isOnShift = true }) {
  const { name, media, totalTickets, totalTicketsPresence } = user;

  // Determinar status do atendente
  let status = 'free'; // Livre
  let statusLabel = 'Livre';

  if (isOnDayOff) {
    status = 'dayoff';
    statusLabel = 'Em folga';
  } else if (!isOnShift) {
    status = 'offshift';
    statusLabel = 'Fora do plantão';
  } else if (totalTickets > 0) {
    if (totalTicketsPresence > 0) {
      status = 'presencial';
      statusLabel = 'Atendendo (Presencial)';
    } else {
      status = 'busy';
      statusLabel = 'Atendendo';
    }
  }

  return (
    <StyledCard status={status}>
      {rankingMode ? (
        <RankingBadge position={position}>
          {position}
        </RankingBadge>
      ) : (
        <StatusIndicator status={status} />
      )}

      <StyledAvatar
        alt={name}
        src={media}
      />

      <UserInfo>
        <UserName>
          {name}
        </UserName>
        <StatusLabel
          sx={{
            ...(status === 'dayoff' && { color: 'error.main', fontWeight: 600, fontSize: '0.75rem' }),
            ...(status === 'offshift' && { color: 'warning.main', fontWeight: 600, fontSize: '0.75rem' }),
            ...(status !== 'dayoff' && status !== 'offshift' && { textTransform: 'uppercase', letterSpacing: '0.5px' }),
          }}
        >
          {statusLabel}
        </StatusLabel>
      </UserInfo>

      <TicketsInfo>
        <TicketRow onClick={() => onTicketClick && onTicketClick(user)}>
          <TicketCount>
            {fShortenNumber(totalTickets)}
          </TicketCount>
          <TicketLabel>
            tickets
          </TicketLabel>
        </TicketRow>

        {totalTicketsPresence > 0 && (
          <PresencialRow>
            <PresencialCount>
              {fShortenNumber(totalTicketsPresence)}
            </PresencialCount>
            <PresencialLabel>
              presencial
            </PresencialLabel>
          </PresencialRow>
        )}
      </TicketsInfo>
    </StyledCard>
  );
}

// Memoizado: sem isso, qualquer re-render do pai (ex.: Monitor) força
// re-render de todos os UserCards mesmo que nada mudou nos props deles.
export default React.memo(UserCard);
